import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@crazyegginc/hatch';

import { SEO } from '/src/components/SEO';
import { DashHeader } from '/src/components/headers/DashHeader';
import { refreshBillingPlanQueries } from '/src/features/billing/mutations';
import { AppLink } from '/src/features/_global/components/AppLink';

const REFRESH_ATTEMPTS = 3;
const REFRESH_INTERVAL_MS = 1500;

function wait(delay) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, delay);
  });
}

export function SubscribeSuccessPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const reason = searchParams.get('reason');
  const [isRefreshingPlan, setIsRefreshingPlan] = useState(status !== 'error');

  const isError = status === 'error';

  useEffect(() => {
    if (isError) return;

    let isCancelled = false;

    async function syncPlanDetails() {
      try {
        for (let attempt = 0; attempt < REFRESH_ATTEMPTS; attempt += 1) {
          await refreshBillingPlanQueries(queryClient);

          if (isCancelled || attempt === REFRESH_ATTEMPTS - 1) return;

          await wait(REFRESH_INTERVAL_MS);
        }
      } finally {
        if (!isCancelled) {
          setIsRefreshingPlan(false);
        }
      }
    }

    syncPlanDetails();

    return () => {
      isCancelled = true;
    };
  }, [isError, queryClient]);

  return (
    <main class="min-w-0 flex-1 bg-white-lilac-500 pb-[150px]">
      <SEO title="Subscription Status" />
      <DashHeader title={isError ? 'Subscription Issue' : 'Subscription Successful'} />
      <div className="flex flex-col p-10">
        <div className="rounded-lg bg-white p-8 shadow-sm">
          <div className="flex flex-col items-center text-center">
            {isError ? (
              <>
                <div className="mb-6">
                  <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <p className="text-body-1 text-lynch-500 mb-4">
                  There was an issue processing your subscription.
                  {reason && <span className="block mt-2 text-sm text-gray-500">Reason: {reason}</span>}
                </p>
                <p className="text-sm text-lynch-400">Please contact support if this issue persists.</p>
              </>
            ) : (
              <>
                <div className="mb-6">
                  <svg
                    className="mx-auto h-16 w-16 text-green-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-body-1 text-lynch-500 mb-4">
                  Thank you! Your subscription may take a minute or two to apply.
                </p>
                <p className="text-sm text-lynch-400">
                  {isRefreshingPlan ? 'Refreshing your plan details...' : 'Your plan details are ready.'}
                </p>
              </>
            )}

            <Button component={AppLink} to="/account/plan" className="mt-6" disabled={!isError && isRefreshingPlan}>
              Go to Plan Details
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
}
